import { RestAPI } from './rest-api.service';
import { BASE_URL } from '../types/constants';
import {
    AdminVideoDetailDto,
    AdminEventCreateDto,
    AdminEventDto,
    AdminEventUpdateDto,
    AdminVideoListItemDto,
    AdminLocationCreateDto,
    AdminLocationDto,
    AdminLocationUpdateDto,
    AdminRoleDto,
    AdminStorageObjectDto,
    AdminStorageObjectListDto,
    AdminStorageObjectLookupRequestDto,
    AdminStorageQuarantineDto,
    AdminUserDto,
    AdminUserCreateDto,
    AdminUserRoleDto,
    AdminUserRolesUpdateDto,
    CollectionCreateDto,
    CollectionDto,
    CollectionUpdateDto,
    ContentProcessorAnalyticsDto,
    ContentProcessorConfigurationDto,
    ContentProcessorConfigurationUpdateDto,
    ContentProcessorEventDto,
    ContentProcessorLogDto,
    ContentProcessorPauseRequestDto,
    ContentProcessorTranscodeTemplateDto,
    ContentProcessorTranscodeTemplateUpdateDto,
    ContentProcessorWorkerDto,
    ContentProcessorWorkerUpdateDto,
    DiagnosticsSnapshotDto,
    EmailLogDto,
    EventTypeCreateDto,
    EventTypeDto,
    EventTypeUpdateDto,
    HighlightCreateDto,
    HighlightDto,
    HighlightUpdateDto,
    ParticipantAttributeTypeCreateDto,
    ParticipantAttributeTypeDto,
    ParticipantAttributeTypeUpdateDto,
    ParticipantAttachedVideoDto,
    ParticipantAttributeValueCreateDto,
    ParticipantAttributeValueDto,
    ParticipantCreateDto,
    ParticipantDto,
    ParticipantUpdateDto,
    PaymentDisputeAlertSummaryDto,
    PaymentOperationsSummaryDto,
    PaymentRecoveryResultDto,
    LegacySubscriptionLinkPreviewDto,
    PromoCreateDto,
    PromoDto,
    PromoUpdateDto,
    ImageDetailDto,
    ImageListItemDto,
    ImagePatchDto,
    ImageUploadInitRequestDto,
    ImageUploadInitResponseDto,
    AdminCollectionAutomationConfigurationDto,
    AdminCollectionAutomationConfigurationUpdateDto,
    AdminHighlightAutomationConfigurationDto,
    AdminHighlightAutomationConfigurationUpdateDto,
    AdminPlaybackConfigurationUpdateDto,
    AdminSiteConfigurationDto,
    AdminSiteConfigurationUpdateDto,
    AdminVideoPlaybackConfigurationUpdateDto,
    TagCreateDto,
    TagDto,
    TagUpdateDto,
    UserSubscriptionSummaryDto,
    PagedResult,
    AdminVideoUpsertDto,
    AdminVideoUploadInitRequestDto,
    AdminVideoUploadInitResponseDto,
    AdminVideoUploadCompletionResultDto,
    AdminVideoMediaHealthDto,
    AdminVideoMediaRepairResultDto,
    AdminVideoMediaReplacementActionResultDto,
    AdminVideoMediaReplacementRequestDto,
    AdminVideoMediaReplacementUploadDto,
    VideoParticipantCreateDto,
    VideoParticipantDto,
    VideoParticipantTypeCreateDto,
    VideoParticipantTypeDto,
    VideoParticipantTypeUpdateDto,
    VideoParticipantUpdateDto
} from '../types/admin.types';

const api = new RestAPI();

export const fetchAdminEvents = async (
    includeArchived = false,
    token?: string | null
): Promise<AdminEventDto[]> => {
    return api.get(`${BASE_URL}/v1/admin/events?includeArchived=${includeArchived}`, token);
};

export const createAdminEvent = async (payload: AdminEventCreateDto, token?: string | null): Promise<AdminEventDto> => {
    return api.post(`${BASE_URL}/v1/admin/events`, payload, token);
};

export const updateAdminEvent = async (id: string, payload: AdminEventUpdateDto, token?: string | null): Promise<AdminEventDto> => {
    return api.put(`${BASE_URL}/v1/admin/events/${id}`, payload, token);
};

export const archiveAdminEvent = async (id: string, token?: string | null): Promise<void> => {
    await api.postVoid(`${BASE_URL}/v1/admin/events/${id}/archive`, null, token);
};

export const restoreAdminEvent = async (id: string, token?: string | null): Promise<void> => {
    await api.postVoid(`${BASE_URL}/v1/admin/events/${id}/restore`, null, token);
};

export const fetchAdminLocations = async (
    includeArchived = false,
    token?: string | null
): Promise<AdminLocationDto[]> => {
    return api.get(`${BASE_URL}/v1/admin/locations?includeArchived=${includeArchived}`, token);
};

export const createAdminLocation = async (
    payload: AdminLocationCreateDto,
    token?: string | null
): Promise<AdminLocationDto> => {
    return api.post(`${BASE_URL}/v1/admin/locations`, payload, token);
};

export const updateAdminLocation = async (
    id: string,
    payload: AdminLocationUpdateDto,
    token?: string | null
): Promise<AdminLocationDto> => {
    return api.put(`${BASE_URL}/v1/admin/locations/${id}`, payload, token);
};

export const archiveAdminLocation = async (id: string, token?: string | null): Promise<void> => {
    await api.postVoid(`${BASE_URL}/v1/admin/locations/${id}/archive`, null, token);
};

export const restoreAdminLocation = async (id: string, token?: string | null): Promise<void> => {
    await api.postVoid(`${BASE_URL}/v1/admin/locations/${id}/restore`, null, token);
};

export const fetchAdminHighlights = async (token?: string | null): Promise<HighlightDto[]> => {
    return api.get(`${BASE_URL}/v1/admin/highlights`, token);
};

export const createAdminHighlight = async (payload: HighlightCreateDto, token?: string | null): Promise<HighlightDto> => {
    return api.post(`${BASE_URL}/v1/admin/highlights`, payload, token);
};

export const updateAdminHighlight = async (id: string, payload: HighlightUpdateDto, token?: string | null): Promise<HighlightDto> => {
    return api.put(`${BASE_URL}/v1/admin/highlights/${id}`, payload, token);
};

export const deleteAdminHighlight = async (id: string, token?: string | null): Promise<void> => {
    await api.delete(`${BASE_URL}/v1/admin/highlights/${id}`, token);
};

export const fetchAdminCollections = async (token?: string | null): Promise<CollectionDto[]> => {
    return api.get(`${BASE_URL}/v1/admin/collections`, token);
};

export const createAdminCollection = async (payload: CollectionCreateDto, token?: string | null): Promise<CollectionDto> => {
    return api.post(`${BASE_URL}/v1/admin/collections`, payload, token);
};

export const updateAdminCollection = async (id: string, payload: CollectionUpdateDto, token?: string | null): Promise<CollectionDto> => {
    return api.put(`${BASE_URL}/v1/admin/collections/${id}`, payload, token);
};

export const deleteAdminCollection = async (id: string, token?: string | null): Promise<void> => {
    await api.delete(`${BASE_URL}/v1/admin/collections/${id}`, token);
};

export const fetchAdminPromos = async (token?: string | null): Promise<PromoDto[]> => {
    return api.get(`${BASE_URL}/v1/admin/promos`, token);
};

export const createAdminPromo = async (payload: PromoCreateDto, token?: string | null): Promise<PromoDto> => {
    return api.post(`${BASE_URL}/v1/admin/promos`, payload, token);
};

export const updateAdminPromo = async (id: string, payload: PromoUpdateDto, token?: string | null): Promise<PromoDto> => {
    return api.put(`${BASE_URL}/v1/admin/promos/${id}`, payload, token);
};

export const deleteAdminPromo = async (id: string, token?: string | null): Promise<void> => {
    await api.delete(`${BASE_URL}/v1/admin/promos/${id}`, token);
};

export const fetchAdminImagesPaged = async (
    page: number,
    pageSize: number,
    q?: string,
    includeArchived = false,
    token?: string | null
): Promise<PagedResult<ImageListItemDto>> => {
    const searchParams = new URLSearchParams({
        q: q ?? '',
        page: page.toString(),
        pageSize: pageSize.toString(),
        includeArchived: includeArchived.toString()
    });
    return api.get(`${BASE_URL}/v1/admin/images/paged?${searchParams.toString()}`, token);
};

export const fetchAdminImage = async (id: string, token?: string | null): Promise<ImageDetailDto> => {
    return api.get(`${BASE_URL}/v1/admin/images/${id}`, token);
};

export const createAdminImageUpload = async (
    payload: ImageUploadInitRequestDto,
    token?: string | null
): Promise<ImageUploadInitResponseDto> => {
    return api.post(`${BASE_URL}/v1/admin/images`, payload, token);
};

export const completeAdminImageUpload = async (id: string, token?: string | null): Promise<ImageDetailDto> => {
    return api.post(`${BASE_URL}/v1/admin/images/${id}/complete-upload`, null, token);
};

export const patchAdminImage = async (
    id: string,
    payload: ImagePatchDto,
    token?: string | null
): Promise<ImageDetailDto> => {
    const response = await fetch(`${BASE_URL}/v1/admin/images/${id}`, {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        method: 'PATCH',
        body: JSON.stringify(payload)
    });

    if (response.status === 401) {
        throw new Error('Token has expired, please re-login or you are not authorized to perform this action.');
    }

    const responseData = await response.json().catch(() => null);
    if (response.ok && responseData) {
        return responseData as ImageDetailDto;
    }

    const errorMessage = responseData?.Title ? `Error processing request: ${responseData.Title}` : 'Unable to update image.';
    throw new Error(errorMessage);
};

export const archiveAdminImage = async (id: string, token?: string | null): Promise<void> => {
    await api.postVoid(`${BASE_URL}/v1/admin/images/${id}/archive`, null, token);
};

export const restoreAdminImage = async (id: string, token?: string | null): Promise<void> => {
    await api.postVoid(`${BASE_URL}/v1/admin/images/${id}/restore`, null, token);
};

export const fetchAdminTags = async (token?: string | null): Promise<TagDto[]> => {
    return api.get(`${BASE_URL}/v1/admin/tags`, token);
};

export const createAdminTag = async (payload: TagCreateDto, token?: string | null): Promise<TagDto> => {
    const response = await fetch(`${BASE_URL}/v1/admin/tags`, {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        method: 'POST',
        body: JSON.stringify(payload)
    });

    if (response.status === 401) {
        throw new Error('Token has expired, please re-login or you are not authorized to perform this action.');
    }

    const responseData = await response.json().catch(() => null);
    if (response.ok && responseData) {
        return responseData as TagDto;
    }

    throw new Error('Unable to create tag.');
};

export const updateAdminTag = async (
    id: string,
    payload: TagUpdateDto,
    token?: string | null
): Promise<{ tag: TagDto | null; conflict: boolean }> => {
    const response = await fetch(`${BASE_URL}/v1/admin/tags/${id}`, {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        method: 'PUT',
        body: JSON.stringify(payload)
    });

    if (response.status === 401) {
        throw new Error('Token has expired, please re-login or you are not authorized to perform this action.');
    }

    const responseData = await response.json().catch(() => null);
    if (response.ok) {
        return { tag: responseData as TagDto, conflict: false };
    }

    if (response.status === 409) {
        const conflictTag = responseData?.ExistingTag ?? responseData?.existingTag ?? responseData;
        return { tag: (conflictTag ?? null) as TagDto | null, conflict: true };
    }

    throw new Error('Unable to update tag.');
};

export const deleteAdminTag = async (id: string, token?: string | null): Promise<void> => {
    await api.delete(`${BASE_URL}/v1/admin/tags/${id}`, token);
};

export const fetchAdminEventTypes = async (token?: string | null): Promise<EventTypeDto[]> => {
    return api.get(`${BASE_URL}/v1/admin/event-types`, token);
};

export const createAdminEventType = async (payload: EventTypeCreateDto, token?: string | null): Promise<EventTypeDto> => {
    return api.post(`${BASE_URL}/v1/admin/event-types`, payload, token);
};

export const updateAdminEventType = async (id: string, payload: EventTypeUpdateDto, token?: string | null): Promise<EventTypeDto> => {
    return api.put(`${BASE_URL}/v1/admin/event-types/${id}`, payload, token);
};

export const deleteAdminEventType = async (id: string, token?: string | null): Promise<void> => {
    await api.delete(`${BASE_URL}/v1/admin/event-types/${id}`, token);
};

export const fetchParticipantAttributeTypes = async (token?: string | null): Promise<ParticipantAttributeTypeDto[]> => {
    return api.get(`${BASE_URL}/v1/admin/participant/attribute/types`, token);
};

export const fetchParticipantAttributeValue = async (
    participantId: string,
    attributeId: string,
    token?: string | null
): Promise<ParticipantAttributeValueDto | null> => {
    const response = await fetch(`${BASE_URL}/v1/admin/participants/${participantId}/attribute/${attributeId}`, {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        method: 'GET'
    });

    if (response.status === 404) {
        return null;
    }

    if (response.status === 401) {
        throw new Error('Token has expired, please re-login or you are not authorized to perform this action.');
    }

    if (!response.ok) {
        throw new Error('Unable to load participant attribute value.');
    }

    return response.json();
};

export const setParticipantAttributeValue = async (
    payload: ParticipantAttributeValueCreateDto,
    token?: string | null
): Promise<void> => {
    await api.postVoid(`${BASE_URL}/v1/admin/participants/${payload.ParticipantId}/attribute/${payload.TypeId}`, payload, token);
};

export const createParticipantAttributeType = async (payload: ParticipantAttributeTypeCreateDto, token?: string | null): Promise<void> => {
    await api.postVoid(`${BASE_URL}/v1/admin/participant/attribute/types`, payload, token);
};

export const updateParticipantAttributeType = async (payload: ParticipantAttributeTypeUpdateDto, token?: string | null): Promise<void> => {
    await api.putVoid(`${BASE_URL}/v1/admin/participant/attribute/types`, payload, token);
};

export const deleteParticipantAttributeType = async (id: string, token?: string | null): Promise<void> => {
    await api.delete(`${BASE_URL}/v1/admin/participant/attribute/types/${id}`, token);
};

export const fetchParticipants = async (token?: string | null, q?: string): Promise<ParticipantDto[]> => {
    const params = new URLSearchParams();
    if (q && q.trim().length > 0) {
        params.set('q', q.trim());
    }

    const query = params.toString();
    const path = query.length > 0
        ? `${BASE_URL}/v1/admin/participants?${query}`
        : `${BASE_URL}/v1/admin/participants`;

    return api.get(path, token);
};

export const createParticipant = async (payload: ParticipantCreateDto, token?: string | null): Promise<void> => {
    await api.postVoid(`${BASE_URL}/v1/admin/participants`, payload, token);
};

export const updateParticipant = async (payload: ParticipantUpdateDto, token?: string | null): Promise<void> => {
    await api.putVoid(`${BASE_URL}/v1/admin/participants`, payload, token);
};

export const deleteParticipant = async (id: string, token?: string | null): Promise<void> => {
    await api.delete(`${BASE_URL}/v1/admin/participants/${id}`, token);
};

export const fetchParticipantVideos = async (participantId: string, token?: string | null): Promise<ParticipantAttachedVideoDto[]> => {
    return api.get(`${BASE_URL}/v1/admin/participants/${participantId}/videos`, token);
};

export const fetchVideoParticipantTypes = async (token?: string | null): Promise<VideoParticipantTypeDto[]> => {
    return api.get(`${BASE_URL}/v1/admin/video/participant/types`, token);
};

export const createVideoParticipantType = async (payload: VideoParticipantTypeCreateDto, token?: string | null): Promise<void> => {
    await api.postVoid(`${BASE_URL}/v1/admin/video/participant/types`, payload, token);
};

export const updateVideoParticipantType = async (payload: VideoParticipantTypeUpdateDto, token?: string | null): Promise<void> => {
    await api.putVoid(`${BASE_URL}/v1/admin/video/participant/types`, payload, token);
};

export const deleteVideoParticipantType = async (id: string, token?: string | null): Promise<void> => {
    await api.delete(`${BASE_URL}/v1/admin/video/participant/types/${id}`, token);
};

export const fetchAdminVideosPaged = async (
    page = 1,
    pageSize = 50,
    query = '',
    tags: string[] = [],
    token?: string | null
): Promise<PagedResult<AdminVideoListItemDto>> => {
    const params = new URLSearchParams();
    if (query && query.trim().length > 0) {
        params.set('q', query.trim());
    }
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    if (tags.length > 0) {
        tags.forEach((tag) => params.append('tags', tag));
    }
    return api.get(`${BASE_URL}/v1/admin/videos/paged?${params.toString()}`, token);
};

export const fetchAdminVideo = async (id: string, token?: string | null): Promise<AdminVideoDetailDto> => {
    return api.get(`${BASE_URL}/v1/admin/videos/${id}`, token);
};

export const createAdminVideoDraft = async (
    payload: AdminVideoUpsertDto,
    token?: string | null
): Promise<AdminVideoDetailDto> => {
    return api.post(`${BASE_URL}/v1/admin/videos/drafts`, payload, token);
};

export const initAdminVideoUpload = async (
    payload: AdminVideoUploadInitRequestDto,
    token?: string | null
): Promise<AdminVideoUploadInitResponseDto> => {
    return api.post(`${BASE_URL}/v1/admin/videos`, payload, token);
};

export const completeAdminVideoUpload = async (
    videoId: string,
    token?: string | null,
    uploadAttemptId?: string | null
): Promise<AdminVideoUploadCompletionResultDto> => {
    const attemptQuery = uploadAttemptId
        ? `?uploadAttemptId=${encodeURIComponent(uploadAttemptId)}`
        : '';
    return api.post(`${BASE_URL}/v1/admin/videos/${videoId}/complete-upload${attemptQuery}`, null, token);
};

export const fetchAdminVideoMediaHealth = async (
    videoId: string,
    token?: string | null
): Promise<AdminVideoMediaHealthDto> => {
    return api.get(`${BASE_URL}/v1/admin/videos/${videoId}/media`, token);
};

export const repairAdminVideoMedia = async (
    videoId: string,
    format: number,
    token?: string | null
): Promise<AdminVideoMediaRepairResultDto> => {
    return api.post(`${BASE_URL}/v1/admin/videos/${videoId}/media/repair`, { Format: format }, token);
};

export const createAdminVideoMediaReplacement = async (
    videoId: string,
    payload: AdminVideoMediaReplacementRequestDto,
    token?: string | null
): Promise<AdminVideoMediaReplacementUploadDto> => {
    return api.post(`${BASE_URL}/v1/admin/videos/${videoId}/media/replacements`, payload, token);
};

export const createAdminVideoMediaReplacementUploadUrl = async (
    videoId: string,
    generationId: string,
    payload: AdminVideoMediaReplacementRequestDto,
    token?: string | null
): Promise<AdminVideoMediaReplacementUploadDto> => {
    return api.post(
        `${BASE_URL}/v1/admin/videos/${videoId}/media/replacements/${generationId}/upload-url`,
        payload,
        token
    );
};

export const completeAdminVideoMediaReplacementUpload = async (
    videoId: string,
    generationId: string,
    uploadAttemptId: string,
    token?: string | null
): Promise<AdminVideoMediaReplacementActionResultDto> => {
    return api.post(
        `${BASE_URL}/v1/admin/videos/${videoId}/media/replacements/${generationId}/complete-upload`,
        { UploadAttemptId: uploadAttemptId },
        token
    );
};

export const retryAdminVideoMediaReplacementPrimary = async (
    videoId: string,
    generationId: string,
    token?: string | null
): Promise<AdminVideoMediaReplacementActionResultDto> => {
    return api.post(
        `${BASE_URL}/v1/admin/videos/${videoId}/media/replacements/${generationId}/retry-primary`,
        null,
        token
    );
};

export const abandonAdminVideoMediaReplacement = async (
    videoId: string,
    generationId: string,
    token?: string | null
): Promise<AdminVideoMediaReplacementActionResultDto> => {
    return api.post(
        `${BASE_URL}/v1/admin/videos/${videoId}/media/replacements/${generationId}/abandon`,
        null,
        token
    );
};

export const upsertAdminVideo = async (
    videoId: string,
    payload: AdminVideoUpsertDto,
    token?: string | null
): Promise<AdminVideoDetailDto> => {
    return api.put(`${BASE_URL}/v1/admin/videos/${videoId}`, payload, token);
};

export const updateAdminVideoPlaybackConfiguration = async (
    videoId: string,
    payload: AdminVideoPlaybackConfigurationUpdateDto,
    token?: string | null
): Promise<AdminVideoDetailDto> => {
    return api.put(`${BASE_URL}/v1/admin/videos/${videoId}/playback`, payload, token);
};

export const fetchVideoParticipants = async (videoId: string, token?: string | null): Promise<VideoParticipantDto[]> => {
    return api.get(`${BASE_URL}/v1/admin/video/${videoId}/participants`, token);
};

export const addVideoParticipant = async (videoId: string, payload: VideoParticipantCreateDto, token?: string | null): Promise<void> => {
    await api.postVoid(`${BASE_URL}/v1/admin/video/${videoId}/participants`, payload, token);
};

export const updateVideoParticipant = async (videoId: string, payload: VideoParticipantUpdateDto, token?: string | null): Promise<void> => {
    await api.putVoid(`${BASE_URL}/v1/admin/video/${videoId}/participants`, payload, token);
};

export const removeVideoParticipant = async (videoId: string, participantId: string, token?: string | null): Promise<void> => {
    await api.delete(`${BASE_URL}/v1/admin/video/${videoId}/participants/${participantId}`, token);
};

export const fetchSiteConfiguration = async (token?: string | null): Promise<AdminSiteConfigurationDto> => {
    return api.get(`${BASE_URL}/v1/admin/siteConfiguration`, token);
};

export const fetchAdminDiagnostics = async (token?: string | null): Promise<DiagnosticsSnapshotDto> => {
    return api.get(`${BASE_URL}/v1/admin/diagnostics`, token);
};

export const fetchPaymentDisputeAlerts = async (
    activeOnly = false,
    limit = 50,
    token?: string | null
): Promise<PaymentDisputeAlertSummaryDto> => {
    const searchParams = new URLSearchParams({
        activeOnly: activeOnly.toString(),
        limit: Math.max(1, Math.min(100, limit)).toString()
    });
    return api.get(`${BASE_URL}/v1/admin/stripe-webhooks/disputes?${searchParams.toString()}`, token);
};

export const fetchPaymentOperations = async (
    token?: string | null
): Promise<PaymentOperationsSummaryDto> => {
    return api.get(`${BASE_URL}/v1/admin/payment-operations`, token);
};

export const retryStripeWebhook = async (
    stripeEventId: string,
    token?: string | null
): Promise<PaymentRecoveryResultDto> => {
    return api.post(
        `${BASE_URL}/v1/admin/stripe-webhooks/${encodeURIComponent(stripeEventId)}/retry`,
        null,
        token
    );
};

export const reconcilePaymentSubscription = async (
    stripeSubscriptionId: string,
    token?: string | null
): Promise<PaymentRecoveryResultDto> => {
    return api.post(
        `${BASE_URL}/v1/admin/payment-operations/subscriptions/${encodeURIComponent(stripeSubscriptionId)}/reconcile`,
        null,
        token
    );
};

export const previewLegacySubscriptionLink = async (
    userId: string,
    stripeSubscriptionId: string,
    token?: string | null
): Promise<LegacySubscriptionLinkPreviewDto> => {
    const params = new URLSearchParams({ userId });
    return api.get(
        `${BASE_URL}/v1/admin/payment-operations/subscriptions/${encodeURIComponent(stripeSubscriptionId)}/legacy-link-preview?${params.toString()}`,
        token
    );
};

export const linkLegacySubscription = async (
    userId: string,
    stripeSubscriptionId: string,
    expectedStripeCustomerId: string,
    confirmEmailMismatch: boolean,
    token?: string | null
): Promise<PaymentRecoveryResultDto> => {
    return api.post(
        `${BASE_URL}/v1/admin/payment-operations/subscriptions/${encodeURIComponent(stripeSubscriptionId)}/legacy-link`,
        { userId, expectedStripeCustomerId, confirmEmailMismatch },
        token
    );
};

export const reconcileVideoPurchase = async (
    purchaseId: string,
    token?: string | null
): Promise<PaymentRecoveryResultDto> => {
    return api.post(
        `${BASE_URL}/v1/admin/payment-operations/video-purchases/${encodeURIComponent(purchaseId)}/reconcile`,
        null,
        token
    );
};

const storageAdminBaseUrl = `${BASE_URL}/v1/admin/storage`;

export const fetchAdminStorageObject = async (
    key: string,
    token?: string | null
): Promise<AdminStorageObjectDto> => {
    const params = new URLSearchParams({ key });
    return api.get(`${storageAdminBaseUrl}/object?${params.toString()}`, token);
};

export const fetchAdminStorageObjects = async (
    prefix: string,
    limit: number,
    continuationToken?: string | null,
    token?: string | null
): Promise<AdminStorageObjectListDto> => {
    const params = new URLSearchParams({
        prefix,
        limit: String(limit)
    });
    if (continuationToken) {
        params.set('continuationToken', continuationToken);
    }

    return api.get(`${storageAdminBaseUrl}/objects?${params.toString()}`, token);
};

export const checkAdminStorageObjects = async (
    payload: AdminStorageObjectLookupRequestDto,
    token?: string | null
): Promise<AdminStorageObjectDto[]> => {
    return api.post(`${storageAdminBaseUrl}/objects/check`, payload, token);
};

export const searchAdminStorageVideoObjects = async (
    videoId: string,
    token?: string | null
): Promise<AdminStorageObjectDto[]> => {
    const params = new URLSearchParams({ videoId });
    return api.get(`${storageAdminBaseUrl}/video-objects?${params.toString()}`, token);
};

export const quarantineAdminStorageObject = async (
    key: string,
    token?: string | null
): Promise<AdminStorageQuarantineDto> => {
    const params = new URLSearchParams({ key });
    return api.post(`${storageAdminBaseUrl}/object/quarantine?${params.toString()}`, null, token);
};

export const fetchAdminStorageQuarantine = async (
    includeFinalized = false,
    token?: string | null
): Promise<AdminStorageQuarantineDto[]> => {
    const params = new URLSearchParams({ includeFinalized: String(includeFinalized) });
    return api.get(`${storageAdminBaseUrl}/quarantine?${params.toString()}`, token);
};

export const restoreAdminStorageQuarantine = async (
    id: string,
    token?: string | null
): Promise<AdminStorageQuarantineDto> => {
    return api.post(`${storageAdminBaseUrl}/quarantine/${encodeURIComponent(id)}/restore`, null, token);
};

export const purgeAdminStorageQuarantine = async (
    id: string,
    token?: string | null
): Promise<AdminStorageQuarantineDto> => {
    return api.delete(`${storageAdminBaseUrl}/quarantine/${encodeURIComponent(id)}`, token);
};

export const markAdminStorageVideoFileDeleted = async (
    key: string,
    token?: string | null
): Promise<AdminStorageObjectDto> => {
    const params = new URLSearchParams({ key });
    return api.post(`${storageAdminBaseUrl}/object/mark-db-deleted?${params.toString()}`, null, token);
};

export const updateSiteConfiguration = async (payload: AdminSiteConfigurationUpdateDto, token?: string | null): Promise<void> => {
    await api.putVoid(`${BASE_URL}/v1/admin/siteConfiguration`, payload, token);
};

export const updatePlaybackConfiguration = async (payload: AdminPlaybackConfigurationUpdateDto, token?: string | null): Promise<void> => {
    await api.putVoid(`${BASE_URL}/v1/admin/siteConfiguration/playback`, payload, token);
};

export const fetchHighlightAutomationConfiguration = async (
    token?: string | null
): Promise<AdminHighlightAutomationConfigurationDto> => {
    return api.get(`${BASE_URL}/v1/admin/siteConfiguration/highlightsAutomation`, token);
};

export const updateHighlightAutomationConfiguration = async (
    payload: AdminHighlightAutomationConfigurationUpdateDto,
    token?: string | null
): Promise<void> => {
    await api.putVoid(`${BASE_URL}/v1/admin/siteConfiguration/highlightsAutomation`, payload, token);
};

export const fetchCollectionAutomationConfiguration = async (
    token?: string | null
): Promise<AdminCollectionAutomationConfigurationDto> => {
    return api.get(`${BASE_URL}/v1/admin/siteConfiguration/collectionsAutomation`, token);
};

export const updateCollectionAutomationConfiguration = async (
    payload: AdminCollectionAutomationConfigurationUpdateDto,
    token?: string | null
): Promise<void> => {
    await api.putVoid(`${BASE_URL}/v1/admin/siteConfiguration/collectionsAutomation`, payload, token);
};

const contentProcessingAdminBaseUrl = `${BASE_URL}/v1/admin/services/content-processing`;

export const fetchContentProcessorConfiguration = async (
    token?: string | null
): Promise<ContentProcessorConfigurationDto> => {
    return api.get(`${contentProcessingAdminBaseUrl}/config`, token);
};

export const updateContentProcessorConfiguration = async (
    payload: ContentProcessorConfigurationUpdateDto,
    token?: string | null
): Promise<ContentProcessorConfigurationDto> => {
    return api.put(`${contentProcessingAdminBaseUrl}/config`, payload, token);
};

export const pauseContentProcessor = async (
    payload: ContentProcessorPauseRequestDto,
    token?: string | null
): Promise<ContentProcessorConfigurationDto> => {
    return api.post(`${contentProcessingAdminBaseUrl}/pause`, payload, token);
};

export const resumeContentProcessor = async (
    token?: string | null
): Promise<ContentProcessorConfigurationDto> => {
    return api.post(`${contentProcessingAdminBaseUrl}/resume`, null, token);
};

export const fetchContentProcessorWorkers = async (
    token?: string | null
): Promise<ContentProcessorWorkerDto[]> => {
    return api.get(`${contentProcessingAdminBaseUrl}/workers`, token);
};

export const fetchContentProcessorTranscodeTemplates = async (
    token?: string | null
): Promise<ContentProcessorTranscodeTemplateDto[]> => {
    return api.get(`${contentProcessingAdminBaseUrl}/templates`, token);
};

export const createContentProcessorTranscodeTemplate = async (
    payload: ContentProcessorTranscodeTemplateUpdateDto,
    token?: string | null
): Promise<ContentProcessorTranscodeTemplateDto> => {
    return api.post(`${contentProcessingAdminBaseUrl}/templates`, payload, token);
};

export const updateContentProcessorTranscodeTemplate = async (
    templateId: string,
    payload: ContentProcessorTranscodeTemplateUpdateDto,
    token?: string | null
): Promise<ContentProcessorTranscodeTemplateDto> => {
    return api.put(`${contentProcessingAdminBaseUrl}/templates/${encodeURIComponent(templateId)}`, payload, token);
};

export const updateContentProcessorWorker = async (
    workerId: string,
    payload: ContentProcessorWorkerUpdateDto,
    token?: string | null
): Promise<ContentProcessorWorkerDto> => {
    return api.put(`${contentProcessingAdminBaseUrl}/workers/${encodeURIComponent(workerId)}`, payload, token);
};

export const deleteContentProcessorWorker = async (
    workerId: string,
    token?: string | null
): Promise<void> => {
    await api.delete(`${contentProcessingAdminBaseUrl}/workers/${encodeURIComponent(workerId)}`, token);
};

export const fetchContentProcessorAnalytics = async (
    fromUtc: string,
    toUtc: string,
    token?: string | null
): Promise<ContentProcessorAnalyticsDto> => {
    const params = new URLSearchParams({ fromUtc, toUtc });
    return api.get(`${contentProcessingAdminBaseUrl}/analytics?${params.toString()}`, token);
};

export const fetchContentProcessorEvents = async (
    page: number,
    pageSize: number,
    filters: {
        fromUtc?: string;
        toUtc?: string;
        severity?: string;
        eventType?: string;
        workerId?: string;
        videoId?: string;
    },
    token?: string | null
): Promise<PagedResult<ContentProcessorEventDto>> => {
    const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize)
    });
    Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim().length > 0) {
            params.set(key, value.trim());
        }
    });
    return api.get(`${contentProcessingAdminBaseUrl}/events?${params.toString()}`, token);
};

export const fetchContentProcessorLogs = async (
    page: number,
    pageSize: number,
    filters: {
        fromUtc?: string;
        toUtc?: string;
        level?: string;
        workerId?: string;
        videoId?: string;
    },
    token?: string | null
): Promise<PagedResult<ContentProcessorLogDto>> => {
    const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize)
    });
    Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim().length > 0) {
            params.set(key, value.trim());
        }
    });
    return api.get(`${contentProcessingAdminBaseUrl}/logs?${params.toString()}`, token);
};

export const fetchEmailLogs = async (token?: string | null): Promise<EmailLogDto[]> => {
    return api.get(`${BASE_URL}/v1/admin/emails`, token);
};

/**
 * Fetch paged email logs ordered by newest first with optional search query.
 */
export const fetchEmailLogsPaged = async (
    pageIndex: number,
    pageSize: number,
    query: string,
    token?: string | null
): Promise<PagedResult<EmailLogDto>> => {
    const search = encodeURIComponent(query);
    return api.get(`${BASE_URL}/v1/admin/emails/paged?page=${pageIndex}&pageSize=${pageSize}&q=${search}`, token);
};

export const fetchAdminUsers = async (includeDisabled = true, token?: string | null): Promise<AdminUserDto[]> => {
    return api.get(`${BASE_URL}/v1/admin/users/users?includeDisabled=${includeDisabled}`, token);
};

export interface AdminUserListFilters {
    query: string;
    role: string;
    accountStatus: '' | 'enabled' | 'disabled';
    passwordStatus: '' | 'ready' | 'reset';
    subscriptionStatus: '' | 'active' | 'canceling' | 'canceled' | 'none';
}

export class AdminUserMutationError extends Error {
    constructor(message: string, public readonly fieldErrors: Record<string, string> = {}) {
        super(message);
        this.name = 'AdminUserMutationError';
    }
}

const buildAdminUserMutationError = (
    responseData: any,
    fallbackMessage: string,
    usernameField: 'userName' | 'username'
): Error => {
    const errors = responseData?.errors ?? responseData?.Errors;
    if (errors && !Array.isArray(errors) && typeof errors === 'object') {
        const fieldErrors = Object.entries(errors).reduce<Record<string, string>>((result, [key, value]) => {
            const message = Array.isArray(value) ? value.find((item) => typeof item === 'string') : value;
            if (typeof message === 'string') {
                const fieldName = /^user(name)?$/i.test(key)
                    ? usernameField
                    : key.charAt(0).toLowerCase() + key.slice(1);
                result[fieldName] = message;
            }
            return result;
        }, {});
        return new AdminUserMutationError(Object.values(fieldErrors)[0] ?? fallbackMessage, fieldErrors);
    }
    if (Array.isArray(errors) && errors.length > 0) {
        const firstError = errors[0];
        const description = firstError?.description ?? firstError?.Description ?? fallbackMessage;
        const code = String(firstError?.code ?? firstError?.Code ?? '');
        const fieldName = /email/i.test(code) ? 'email' : /user/i.test(code) ? usernameField : '';
        return new AdminUserMutationError(description, fieldName ? { [fieldName]: description } : {});
    }

    if (typeof responseData === 'string' && responseData.trim().length > 0) {
        return new Error(responseData);
    }

    const message = responseData?.detail
        ?? responseData?.Detail
        ?? responseData?.message
        ?? responseData?.Message;
    return new Error(typeof message === 'string' && message.trim().length > 0 ? message : fallbackMessage);
};

/**
 * Fetch paged admin users with optional search, include-disabled toggle, and role filter.
 */
export const fetchAdminUsersPaged = async (
    pageIndex: number,
    pageSize: number,
    filters: AdminUserListFilters,
    token?: string | null
): Promise<PagedResult<AdminUserDto>> => {
    const params = new URLSearchParams({
        includeDisabled: 'true',
        page: String(pageIndex),
        pageSize: String(pageSize),
        q: filters.query
    });

    if (filters.role.trim().length > 0) {
        params.set('role', filters.role.trim());
    }
    if (filters.accountStatus) {
        params.set('enabled', String(filters.accountStatus === 'enabled'));
    }
    if (filters.passwordStatus) {
        params.set('requiresPasswordReset', String(filters.passwordStatus === 'reset'));
    }
    if (filters.subscriptionStatus) {
        params.set('subscriptionStatus', filters.subscriptionStatus);
    }

    return api.get(
        `${BASE_URL}/v1/admin/users/users/paged?${params.toString()}`,
        token
    );
};

export const fetchAdminUser = async (userId: string, token?: string | null): Promise<AdminUserDto> => {
    return api.get(`${BASE_URL}/v1/admin/users/user?userId=${encodeURIComponent(userId)}`, token);
};

export const createAdminUser = async (payload: AdminUserCreateDto, token?: string | null): Promise<AdminUserDto> => {
    const response = await fetch(`${BASE_URL}/v1/admin/users/user`, {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        method: 'PUT',
        body: JSON.stringify(payload)
    });

    if (response.status === 401) {
        throw new Error('Token has expired, please re-login or you are not authorized to perform this action.');
    }

    const rawResponse = await response.text();
    let responseData: any = null;
    if (rawResponse.trim().length > 0) {
        try {
            responseData = JSON.parse(rawResponse);
        } catch {
            responseData = rawResponse;
        }
    }

    if (!response.ok) {
        throw buildAdminUserMutationError(responseData, 'Unable to create user.', 'userName');
    }

    if (!responseData || typeof responseData !== 'object' || typeof responseData.userId !== 'string') {
        throw new Error('The created user response did not include a stable user ID.');
    }

    return responseData as AdminUserDto;
};

export const updateAdminUser = async (payload: AdminUserDto, token?: string | null): Promise<AdminUserDto> => {
    const response = await fetch(`${BASE_URL}/v1/admin/users/user/${encodeURIComponent(payload.userId)}`, {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        method: 'PATCH',
        body: JSON.stringify(payload)
    });

    if (response.status === 401) {
        throw new Error('Token has expired, please re-login or you are not authorized to perform this action.');
    }

    const responseData = await response.json().catch(() => null);
    if (!response.ok) {
        throw buildAdminUserMutationError(responseData, 'Unable to update user.', 'username');
    }

    return responseData as AdminUserDto;
};

export const replaceAdminUserRoles = async (
    payload: AdminUserRolesUpdateDto,
    token?: string | null
): Promise<string[]> => {
    return api.put(`${BASE_URL}/v1/admin/role/user/roles`, payload, token);
};

export const disableAdminUser = async (userId: string, token?: string | null): Promise<void> => {
    await api.delete(`${BASE_URL}/v1/admin/users/user?userId=${encodeURIComponent(userId)}`, token);
};

export const enableAdminUser = async (userId: string, token?: string | null): Promise<void> => {
    await api.postVoid(`${BASE_URL}/v1/admin/users/user/enable?userId=${encodeURIComponent(userId)}`, null, token);
};

export const sendAdminUserPasswordResetEmail = async (userId: string, token?: string | null): Promise<void> => {
    await api.postVoid(`${BASE_URL}/v1/admin/users/user/reset-password-email?userId=${encodeURIComponent(userId)}`, null, token);
};

export const cancelAdminUserSubscription = async (userId: string, token?: string | null): Promise<void> => {
    await api.postVoid(`${BASE_URL}/v1/admin/users/user/cancel-subscription?userId=${encodeURIComponent(userId)}`, null, token);
};

export const syncAdminUserSubscription = async (
    userId: string,
    token?: string | null
): Promise<UserSubscriptionSummaryDto> => {
    return api.post(
        `${BASE_URL}/v1/admin/users/user/sync-subscription?userId=${encodeURIComponent(userId)}`,
        null,
        token
    );
};

export const fetchAdminRoles = async (token?: string | null): Promise<AdminRoleDto[]> => {
    return api.get(`${BASE_URL}/v1/admin/role/roles`, token);
};

export const addAdminUserToRole = async (payload: AdminUserRoleDto, token?: string | null): Promise<void> => {
    await api.putVoid(`${BASE_URL}/v1/admin/role/user`, payload, token);
};

export const removeAdminUserFromRole = async (payload: AdminUserRoleDto, token?: string | null): Promise<void> => {
    const response = await fetch(`${BASE_URL}/v1/admin/role/user`, {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        method: 'DELETE',
        body: JSON.stringify(payload)
    });

    if (response.status === 401) {
        throw new Error('Token has expired, please re-login or you are not authorized to perform this action.');
    }

    if (!response.ok) {
        throw new Error('Unable to remove user role.');
    }
};

export const fetchAdminUserSubscriptionSummaries = async (
    includeDisabled = true,
    token?: string | null,
    signal?: AbortSignal
): Promise<UserSubscriptionSummaryDto[]> => {
    const response = await fetch(`${BASE_URL}/v1/admin/users/users/subscription-summaries?includeDisabled=${includeDisabled}`, {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        method: 'GET',
        signal
    });

    if (response.status === 401) {
        throw new Error('Token has expired, please re-login or you are not authorized to perform this action.');
    }

    if (!response.ok) {
        throw new Error('Unable to load user subscription summaries.');
    }

    return response.json();
};

/**
 * Fetch subscription summary for one user, used by the edit panel lazy load flow.
 */
export const fetchAdminUserSubscriptionSummary = async (
    userId: string,
    token?: string | null,
    signal?: AbortSignal
): Promise<UserSubscriptionSummaryDto> => {
    const response = await fetch(`${BASE_URL}/v1/admin/users/user/subscription-summary?userId=${encodeURIComponent(userId)}`, {
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        method: 'GET',
        signal
    });

    if (response.status === 401) {
        throw new Error('Token has expired, please re-login or you are not authorized to perform this action.');
    }

    if (response.status === 404) {
        throw new Error('User not found.');
    }

    if (!response.ok) {
        throw new Error('Unable to load user subscription summary.');
    }

    return response.json();
};

export const uploadToPresignedUrl = async (
    url: string,
    file: File,
    extraHeaders: Record<string, string> = {}
): Promise<void> => {
    const response = await fetch(url, {
        method: 'PUT',
        headers: {
            ...extraHeaders,
            'Content-Type': file.type || 'video/mp4'
        },
        body: file
    });

    if (!response.ok) {
        throw new Error('Failed to upload file to storage.');
    }
};
